import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import Badge from "../../components/ui-components/Badge.jsx";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import { useKeycloak } from "@react-keycloak/web";
import { useFilterStoreWithDayjsDates } from "../../store/filterStore.js";
import moment from "moment";

const API_URL = process.env.REACT_APP_API_URL;

const TYPE_CONFIG = {
  data_import:       { label: "Import",    color: "#C6F7E2" },
  data_load:         { label: "Import",    color: "#C6F7E2" },
  data_transform:    { label: "Transform", color: "#B3ECFF" },
  allocation:        { label: "Allocation", color: "#FFE3C4" },
  data_forecast:     { label: "Forecast",  color: "#D9E8FF" },
  report_generation: { label: "Report",    color: "#FFF3C4" },
  pipeline:          { label: "Pipeline",  color: "#E8DAFF" },
};

function getTypeInfo(row) {
  if (row.job_type && TYPE_CONFIG[row.job_type]) {
    return TYPE_CONFIG[row.job_type];
  }
  const cat = (row.category || "").toLowerCase();
  if (cat === "import") return { label: "Import", color: "#C6F7E2" };
  if (cat === "scheduler") return { label: "Scheduler", color: "#B3ECFF" };
  if (cat === "server") return { label: "System", color: "#E4E7EB" };
  if (cat === "api") return { label: "API", color: "#E4E7EB" };
  return { label: row.category || "—", color: "#E4E7EB" };
}

function getSourceInfo(row) {
  if (row.pipeline_id && row.job_type) {
    const stageNames = { data_import: "Import", data_load: "Import", data_transform: "Transform", allocation: "Allocation", data_forecast: "Forecast" };
    const stageName = stageNames[row.job_type] || row.job_type;
    return { label: `Pipeline › ${stageName}`, link: "/data-pipeline" };
  }
  if (row.job_type === "report_generation" && row.schedule_id) {
    return { label: "Reporting Job", link: `/reporting-jobs/${row.schedule_id}` };
  }
  if (row.schedule_id) {
    return { label: `Schedule #${row.schedule_id}`, link: `/data-pipeline` };
  }
  return { label: "—", link: null };
}

function calculateDuration(startTime, endTime) {
  if (endTime === "1900-01-01T00:00:00Z") return "";
  return moment.utc(moment(endTime).diff(moment(startTime))).format("HH:mm:ss");
}

const PAGE_SIZE = 100;

const ActivityLog = () => {
  const [logging, setLogging] = useState([]);
  const [sortColumn, setSortColumn] = useState({ column: "date_time", direction: "desc" });
  const [searchQuery, setSearchQuery] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const { keycloak } = useKeycloak();
  const { startDate, endDate } = useFilterStoreWithDayjsDates();
  const navigate = useNavigate();

  const sortColumnRef = useRef();
  sortColumnRef.current = sortColumn;
  const loadedCountRef = useRef(0);

  const openDetails = (logID) => navigate("/data-pipeline/log/" + logID);

  function sortTable(column, defaultOrder = "asc") {
    let direction;
    if (column === sortColumnRef.current.column) {
      direction = sortColumnRef.current.direction === "asc" ? "desc" : "asc";
    } else {
      direction = defaultOrder;
    }
    setSortColumn({ column, direction });
  }

  function buildRequest() {
    return {
      filters: [],
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD"),
      sort_column: sortColumnRef.current.column,
      sort_direction: sortColumnRef.current.direction,
    };
  }

  // Fetch a window of log rows. append=false replaces the list (initial load / auto-refresh);
  // append=true extends it (Load more). A full page back implies there may be more to load.
  function fetchLogging(offset, count, append) {
    if (!keycloak.token) return;
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + keycloak.token);
    if (append) setLoadingMore(true);
    fetch(API_URL + `/get-logging?limit=${encodeURIComponent(count)}&offset=${encodeURIComponent(offset)}`, {
      body: JSON.stringify(buildRequest()),
      method: "POST",
      headers,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        const rows = json?.Logging || [];
        setLogging((prev) => {
          const next = append ? [...prev, ...rows] : rows;
          loadedCountRef.current = next.length;
          return next;
        });
        setHasMore(rows.length >= (append ? PAGE_SIZE : count));
      })
      .catch(() => {
        if (!append) { setLogging([]); loadedCountRef.current = 0; setHasMore(false); }
      })
      .finally(() => { if (append) setLoadingMore(false); });
  }

  const loadMore = () => fetchLogging(loadedCountRef.current, PAGE_SIZE, true);

  // Reset to the first page whenever the token, sort, or period changes.
  useEffect(() => {
    loadedCountRef.current = 0;
    fetchLogging(0, PAGE_SIZE, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.token, sortColumn, startDate, endDate]);

  // Auto-refresh the currently-loaded window every 10s (preserves any extra pages loaded).
  useEffect(() => {
    const interval = setInterval(() => {
      fetchLogging(0, Math.max(PAGE_SIZE, loadedCountRef.current), false);
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.token, sortColumn, startDate, endDate]);

  function SortHeader({ column, label, defaultOrder = "desc" }) {
    return (
      <span
        className="cursor-pointer select-none"
        onClick={(e) => { e.stopPropagation(); sortTable(column, defaultOrder); }}
      >
        {label}
        {sortColumn.column === column && sortColumn.direction === "asc" && (
          <FontAwesomeIcon icon={icon({ name: "caret-up", family: "sharp", style: "solid" })} className="ml-2" />
        )}
        {sortColumn.column === column && sortColumn.direction !== "asc" && (
          <FontAwesomeIcon icon={icon({ name: "caret-down", family: "sharp", style: "solid" })} className="ml-2" />
        )}
      </span>
    );
  }

  function TypeBadge({ row }) {
    const { label, color } = getTypeInfo(row);
    return (
      <div
        style={{ backgroundColor: color }}
        className="inline-block text-center rounded-lg text-sm font-normal cursor-default px-2 py-0.5 min-w-[65px]"
      >
        {label}
      </div>
    );
  }

  function SourceCell({ row }) {
    const { label, link } = getSourceInfo(row);
    if (link) {
      return (
        <button
          className="text-primary hover:text-primary-hover text-sm text-left cursor-pointer"
          onClick={(e) => { e.stopPropagation(); navigate(link); }}
        >
          {label}
        </button>
      );
    }
    return <span className="text-sm text-text-3">{label}</span>;
  }

  const columnDefs = useMemo(
    () => [
      {
        accessor: "date_time",
        Header: () => <SortHeader column="date_time" label="Date / time" />,
        Cell: ({ value }) => <span className="text-text-2">{moment(value).format("dddd DD-MM-YYYY HH:mm")}</span>,
        width: 220,
        minWidth: 200,
        maxWidth: 260,
      },
      {
        accessor: "action",
        Header: () => <SortHeader column="action" label="Action" />,
        Cell: ({ value }) => <span className="font-medium text-text truncate">{value}</span>,
        flex: 1,
        minWidth: 240,
      },
      {
        accessor: "job_type",
        Header: () => <span>Type</span>,
        Cell: ({ row }) => <TypeBadge row={row.original} />,
        width: 110,
        minWidth: 90,
      },
      {
        accessor: "schedule_id",
        Header: () => <span>Source</span>,
        Cell: ({ row }) => <SourceCell row={row.original} />,
        width: 150,
        minWidth: 120,
      },
      {
        accessor: "end_date_time",
        Header: () => <span>Duration</span>,
        Cell: ({ row }) => <span className="text-text-2">{calculateDuration(row.original.date_time, row.original.end_date_time)}</span>,
        width: 100,
        minWidth: 80,
      },
      {
        accessor: "status",
        align: "center",
        Header: () => <SortHeader column="status" label="Status" />,
        Cell: ({ value }) => <Badge status={value} />,
        width: 120,
        minWidth: 100,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortColumn]
  );

  // Client-side search across the loaded rows (matches the /budgets table behavior).
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return logging;
    return logging.filter((row) =>
      [
        row.action,
        getTypeInfo(row).label,
        getSourceInfo(row).label,
        row.status,
        moment(row.date_time).format("dddd DD-MM-YYYY HH:mm"),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [logging, searchQuery]);

  const noun = filtered.length === 1 ? "entry" : "entries";

  return (
    <div className="mr-6">
      <TableCard
        title="Activity log"
        subtitle={searchQuery ? `Showing ${filtered.length} ${noun} matching "${searchQuery}"` : `Showing ${filtered.length} ${noun}`}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search activity…"
      >
        {logging.length === 0 ? (
          <EmptyState
            icon={icon({ name: "clock-rotate-left", style: "light" })}
            title="No activity yet"
            message="Pipeline and report runs will appear here."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={icon({ name: "magnifying-glass" })}
            title="No matching activity"
            message={`No entries match "${searchQuery}".`}
            action={{
              label: "Clear search",
              onClick: () => setSearchQuery(""),
              icon: icon({ name: "xmark", style: "regular" }),
              variant: "secondary",
            }}
          />
        ) : (
          <>
            <FlexTable
              columns={columnDefs}
              data={filtered}
              onRowClick={(id) => openDetails(id)}
              getRowId={(r) => r.id}
              rowAriaLabel={(r) => `View activity entry ${r.action || r.id}`}
            />
            {hasMore && (
              <div className="flex justify-center pt-3">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-1.5 rounded-ctrl border border-card-bd bg-card px-4 py-1.5 text-[13px] font-medium text-text-2 transition-colors hover:bg-surface-2 hover:text-text disabled:opacity-50"
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </TableCard>
    </div>
  );
};

export default ActivityLog;
